import { useState, useEffect } from 'react';
import { deviceController } from '../../controllers/deviceController';
import { Plus, Search, Eye, Edit2, Trash2, X, Layers, Smartphone, Users } from 'lucide-react';

// Device Groups use deviceController.getAll with group filter
const DeviceGroupsPage = () => {
  const [allDevices, setAllDevices] = useState([]);
  const [groups, setGroups] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showView, setShowView] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [selected, setSelected] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', policy: '', platform: 'All' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    const result = await deviceController.getAll();
    if (result.success) {
      setAllDevices(result.data);
      // Build groups from device.group field
      const groupMap = {};
      result.data.forEach(d => {
        const gName = d.group || 'Ungrouped';
        if (!groupMap[gName]) groupMap[gName] = { name: gName, devices: [], description: '', policy: d.policyName || '' };
        groupMap[gName].devices.push(d);
      });
      setGroups(Object.values(groupMap));
    }
    setLoading(false);
  };

  const filtered = groups.filter(g => !searchQuery || g.name.toLowerCase().includes(searchQuery.toLowerCase()));

  const handleInput = e => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleAddSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    // Groups are managed via device.group field; this creates a placeholder group
    // In real app: call group API endpoint
    await new Promise(r => setTimeout(r, 500));
    setGroups(prev => [...prev, { name: formData.name, description: formData.description, policy: formData.policy, devices: [] }]);
    setShowAdd(false); setSubmitting(false);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    await new Promise(r => setTimeout(r, 500));
    setGroups(prev => prev.map(g => g.name === selected.name ? { ...g, ...formData } : g));
    setShowEdit(false); setSubmitting(false);
  };

  const handleDelete = async () => {
    setGroups(prev => prev.filter(g => g.name !== selected.name));
    setShowDelete(false);
  };

  const s = {
    page: { padding: 0 },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' },
    titleWrap: { display: 'flex', alignItems: 'center', gap: '12px' },
    icon: { width: '44px', height: '44px', borderRadius: '12px', backgroundColor: '#8b5cf620', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    title: { fontSize: '26px', fontWeight: '700', color: '#1e293b', margin: 0 },
    subtitle: { fontSize: '13px', color: '#64748b', marginTop: '2px' },
    btnPrimary: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 18px', backgroundColor: '#8b5cf6', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
    btnSecond: { backgroundColor: '#fff', color: '#1e293b', border: '1px solid #e2e8f0' },
    btnDanger: { backgroundColor: '#ef4444', color: '#fff', border: 'none' },
    filterBar: { display: 'flex', gap: '10px', marginBottom: '18px' },
    searchWrap: { flex: 1, minWidth: '220px', position: 'relative' },
    searchIn: { width: '100%', padding: '10px 10px 10px 38px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
    searchIco: { position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' },
    groupCard: { backgroundColor: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #f1f5f9', cursor: 'pointer', transition: 'box-shadow 0.2s' },
    groupName: { fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '4px' },
    groupDesc: { fontSize: '12px', color: '#64748b', marginBottom: '14px' },
    groupMeta: { display: 'flex', gap: '14px' },
    metaItem: { display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: '#64748b' },
    actRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '14px', paddingTop: '12px', borderTop: '1px solid #f1f5f9' },
    actBtn: { padding: '5px', backgroundColor: 'transparent', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' },
    modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    mContent: { backgroundColor: '#fff', borderRadius: '14px', width: '90%', maxWidth: '500px', maxHeight: '90vh', overflow: 'auto' },
    mHeader: { padding: '18px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    mTitle: { fontSize: '18px', fontWeight: '600', color: '#1e293b', margin: 0 },
    mBody: { padding: '24px' },
    mFooter: { padding: '14px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '10px' },
    fGroup: { marginBottom: '16px' },
    label: { display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: '#374151' },
    input: { width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
    textarea: { width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', resize: 'vertical', minHeight: '80px', boxSizing: 'border-box' },
  };

  const platformColors = { Android: '#34a853', iOS: '#007aff', Windows: '#0078d4', macOS: '#555', All: '#8b5cf6' };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div style={s.titleWrap}>
          <div style={s.icon}><Layers size={22} color="#8b5cf6" /></div>
          <div><h1 style={s.title}>Device Groups</h1><div style={s.subtitle}>{groups.length} groups · {allDevices.length} total devices</div></div>
        </div>
        <button style={s.btnPrimary} onClick={() => { setFormData({ name: '', description: '', policy: '', platform: 'All' }); setShowAdd(true); }}><Plus size={15} /> New Group</button>
      </div>

      <div style={s.filterBar}>
        <div style={s.searchWrap}>
          <Search size={15} style={s.searchIco} />
          <input type="text" placeholder="Search groups..." style={s.searchIn} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </div>
      </div>

      {loading ? <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading groups...</div> : (
        <div style={s.grid}>
          {filtered.map(group => {
            const activeCount = group.devices.filter(d => d.status === 'Active').length;
            const platforms = [...new Set(group.devices.map(d => d.platform))];
            return (
              <div key={group.name} style={s.groupCard}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)'}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '10px', backgroundColor: '#8b5cf620', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Layers size={18} color="#8b5cf6" />
                  </div>
                  <div style={{ display: 'flex', gap: '3px' }}>
                    {platforms.slice(0, 3).map(p => (
                      <span key={p} style={{ fontSize: '9px', fontWeight: '700', padding: '2px 6px', borderRadius: '6px', backgroundColor: `${platformColors[p] || '#6b7280'}20`, color: platformColors[p] || '#6b7280' }}>{p}</span>
                    ))}
                  </div>
                </div>
                <div style={s.groupName}>{group.name}</div>
                <div style={s.groupDesc}>{group.description || 'No description'}</div>
                {group.policy && <div style={{ fontSize: '11px', color: '#8b5cf6', marginBottom: '10px' }}>Policy: {group.policy}</div>}
                <div style={s.groupMeta}>
                  <div style={s.metaItem}><Smartphone size={12} /> {group.devices.length} devices</div>
                  <div style={s.metaItem}><div style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#10b981' }} />{activeCount} active</div>
                </div>
                <div style={s.actRow}>
                  <div style={{ display: 'flex', gap: '2px' }}>
                    <button style={s.actBtn} onClick={() => { setSelected(group); setShowView(true); }}><Eye size={14} color="#64748b" /></button>
                    <button style={s.actBtn} onClick={() => { setSelected(group); setFormData({ name: group.name, description: group.description || '', policy: group.policy || '', platform: 'All' }); setShowEdit(true); }}><Edit2 size={14} color="#3b82f6" /></button>
                    <button style={s.actBtn} onClick={() => { setSelected(group); setShowDelete(true); }}><Trash2 size={14} color="#ef4444" /></button>
                  </div>
                  <span style={{ fontSize: '11px', color: '#94a3b8' }}>{group.devices.length > 0 ? `${Math.round((activeCount / group.devices.length) * 100)}% active` : 'Empty'}</span>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && <div style={{ padding: '40px', textAlign: 'center', color: '#64748b', gridColumn: '1/-1' }}>No groups found.</div>}
        </div>
      )}

      {/* Add Modal */}
      {showAdd && (
        <div style={s.modal} onClick={() => setShowAdd(false)}>
          <div style={s.mContent} onClick={e => e.stopPropagation()}>
            <div style={s.mHeader}><h2 style={s.mTitle}>Create Device Group</h2><button style={s.actBtn} onClick={() => setShowAdd(false)}><X size={18} /></button></div>
            <form onSubmit={handleAddSubmit}>
              <div style={s.mBody}>
                <div style={s.fGroup}><label style={s.label}>Group Name *</label><input name="name" style={s.input} value={formData.name} onChange={handleInput} required /></div>
                <div style={s.fGroup}><label style={s.label}>Description</label><textarea name="description" style={s.textarea} value={formData.description} onChange={handleInput} /></div>
                <div style={s.fGroup}><label style={s.label}>Default Policy</label><input name="policy" style={s.input} value={formData.policy} onChange={handleInput} placeholder="e.g. Corporate Security Policy" /></div>
                <div style={s.fGroup}><label style={s.label}>Platform</label>
                  <select name="platform" style={s.input} value={formData.platform} onChange={handleInput}>
                    <option value="All">All Platforms</option><option value="Android">Android</option><option value="iOS">iOS</option><option value="Windows">Windows</option>
                  </select>
                </div>
              </div>
              <div style={s.mFooter}><button type="button" style={{ ...s.btnPrimary, ...s.btnSecond }} onClick={() => setShowAdd(false)}>Cancel</button><button type="submit" style={s.btnPrimary} disabled={submitting}>{submitting ? 'Creating...' : 'Create Group'}</button></div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEdit && selected && (
        <div style={s.modal} onClick={() => setShowEdit(false)}>
          <div style={s.mContent} onClick={e => e.stopPropagation()}>
            <div style={s.mHeader}><h2 style={s.mTitle}>Edit Group</h2><button style={s.actBtn} onClick={() => setShowEdit(false)}><X size={18} /></button></div>
            <form onSubmit={handleEditSubmit}>
              <div style={s.mBody}>
                <div style={s.fGroup}><label style={s.label}>Group Name *</label><input name="name" style={s.input} value={formData.name} onChange={handleInput} required /></div>
                <div style={s.fGroup}><label style={s.label}>Description</label><textarea name="description" style={s.textarea} value={formData.description} onChange={handleInput} /></div>
                <div style={s.fGroup}><label style={s.label}>Default Policy</label><input name="policy" style={s.input} value={formData.policy} onChange={handleInput} /></div>
              </div>
              <div style={s.mFooter}><button type="button" style={{ ...s.btnPrimary, ...s.btnSecond }} onClick={() => setShowEdit(false)}>Cancel</button><button type="submit" style={s.btnPrimary} disabled={submitting}>{submitting ? 'Saving...' : 'Save'}</button></div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showView && selected && (
        <div style={s.modal} onClick={() => setShowView(false)}>
          <div style={{ ...s.mContent, maxWidth: '600px' }} onClick={e => e.stopPropagation()}>
            <div style={s.mHeader}><h2 style={s.mTitle}>{selected.name}</h2><button style={s.actBtn} onClick={() => setShowView(false)}><X size={18} /></button></div>
            <div style={s.mBody}>
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Devices in this group</div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#8b5cf6' }}>{selected.devices.length}</div>
              </div>
              {selected.devices.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {selected.devices.slice(0, 8).map(d => (
                    <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Smartphone size={14} color={d.getPlatformColor()} />
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: '500' }}>{d.deviceName}</div>
                          <div style={{ fontSize: '11px', color: '#64748b' }}>{d.assignedTo || 'Unassigned'}</div>
                        </div>
                      </div>
                      <span style={{ fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '8px', backgroundColor: `${d.getStatusColor()}15`, color: d.getStatusColor() }}>{d.status}</span>
                    </div>
                  ))}
                  {selected.devices.length > 8 && <div style={{ fontSize: '12px', color: '#64748b', textAlign: 'center' }}>+{selected.devices.length - 8} more devices</div>}
                </div>
              )}
            </div>
            <div style={s.mFooter}><button style={s.btnPrimary} onClick={() => setShowView(false)}>Close</button></div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDelete && selected && (
        <div style={s.modal} onClick={() => setShowDelete(false)}>
          <div style={{ ...s.mContent, maxWidth: '380px' }} onClick={e => e.stopPropagation()}>
            <div style={s.mHeader}><h2 style={s.mTitle}>Delete Group</h2><button style={s.actBtn} onClick={() => setShowDelete(false)}><X size={18} /></button></div>
            <div style={s.mBody}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ width: '56px', height: '56px', borderRadius: '50%', backgroundColor: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}><Trash2 size={24} color="#ef4444" /></div>
                <p style={{ color: '#1e293b', fontWeight: '500', marginBottom: '6px' }}>Delete "{selected.name}"?</p>
                <p style={{ fontSize: '13px', color: '#64748b' }}>Devices will be moved to Ungrouped.</p>
              </div>
            </div>
            <div style={s.mFooter}><button style={{ ...s.btnPrimary, ...s.btnSecond }} onClick={() => setShowDelete(false)}>Cancel</button><button style={{ ...s.btnPrimary, ...s.btnDanger }} onClick={handleDelete}>Delete</button></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceGroupsPage;
